import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faTrashAlt, faUpload } from '@fortawesome/pro-regular-svg-icons';
import Spinner from "../client/Spinner";
export const defaultSizeLimit = 5242880;
const bytesToMegabytes = bytes => (Number(bytes) / (1024 * 1024)).toFixed(0);
export const defaultSizeLimitMB = `${bytesToMegabytes(5242880)}MB`;
export const defaultTypes = 'image/jpeg,image/png';
export const defaultTypesArray = defaultTypes.split(',');
export const fileTooLargeError = `This file is too large. Files cannot exceed ${defaultSizeLimitMB}.`;
export const unsupportedFileError = `This is not a supported file type. Please upload a PNG or JPEG.`;
export const fileChangeTooLargeError = `Sorry, we can't change the file as the new file is too large. Files cannot exceed ${defaultSizeLimitMB}.`;
export const genericError = `Something went wrong. Please try uploading again.`;
const addUnique = (array, item) => {
  const set = new Set(array);
  set.add(item);
  return [...set];
};
const ImageRemoveButton = ({
  removeFile
}) => {
  const [isHovered, setHover] = useState(false);
  const className = `photo-upload--image-remove${isHovered ? ` remove-is-hovered` : ``}`;
  return <div className={className}>
      <div className="image-remove-btn--text">Delete</div>
      <button className="image--remove-btn" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onClick={removeFile}>
        <div className="image--remove-btn--button">
          <FontAwesomeIcon className="image--remove-btn--icon" icon={faTimes} role="img" title="Remove Image" />
        </div>
      </button>
    </div>;
};
const DeleteImage = ({
  removeFile
}) => {
  return <button className="foundry--button photo-upload--delete" onClick={removeFile}>
      <FontAwesomeIcon className="delete-image-btn--icon" icon={faTrashAlt} role="img" title="Delete Image" />
      <span className="delete-image-btn--text">Delete Image</span>
    </button>;
};
const ChangeImage = ({
  fileInput
}) => {
  return <button className="foundry--button photo-upload--change" onClick={() => fileInput.current.click()}>
      <FontAwesomeIcon className="change-image-btn--icon" icon={faUpload} role="img" title="Change Image" />
      <span className="change-image-btn--text">Change Image</span>
    </button>;
};
export const SinglePhotoInfo = ({
  file,
  sizeLimit,
  fileInput,
  removeFile,
  reportError = () => {}
}) => {
  const {
    name,
    size,
    type
  } = file;
  return <div className="photo-upload--single-photo--container">
      <PreviewImage file={file} type={type} name={name} size={size} sizeLimit={sizeLimit} reportFileError={reportError} />
      <div className="photo-upload--wrapper">
        <div className="photo-upload-file--name">{name}</div>
        <div className="photo-upload--btns">
          <ChangeImage fileInput={fileInput} />
          <DeleteImage removeFile={removeFile} />
        </div>
      </div>
    </div>;
};
const PreviewImage = ({
  file,
  size,
  type,
  sizeLimit = defaultSizeLimit,
  name,
  acceptedTypes = defaultTypesArray,
  removeFile,
  reportFileError = () => {}
}) => {
  const {
    url
  } = file;
  const [imageUrl, setImageUrl] = useState();
  const [errorState, setErrorState] = useState('');
  const overSize = size > sizeLimit;
  const wrongType = acceptedTypes.indexOf(type) === -1;
  const tagError = msg => {
    reportFileError(true);
    setErrorState(msg);
  };
  const unTagError = () => {
    reportFileError(false);
    setErrorState('');
  };
  useEffect(() => {
    if (file) {
      unTagError();
      if (overSize) {
        tagError(fileTooLargeError);
        return unTagError;
      } else if (wrongType) {
        tagError(unsupportedFileError);
        return unTagError;
      } else if (file instanceof File || url) {
        const reader = new FileReader();
        reader.onload = e => {
          setImageUrl(e.target.result);
        };
        reader.onerror = () => {
          tagError(genericError);
        };
        try {
          const blobFile = url ? fetch(url).then(res => res.blob()) : Promise.resolve(file);
          blobFile.then(blob => reader.readAsDataURL(blob)).catch(() => tagError(genericError));
        } catch (e) {
          tagError(genericError);
        }
        return () => {
          unTagError();
          reader.abort();
        };
      }
    }
  }, [file]);
  if (errorState) {
    return <div className="photo-upload--preview-image image--error image--error-size">
        <span>{errorState}</span>
        <ImageRemoveButton removeFile={removeFile} />
      </div>;
  }
  if (!imageUrl) {
    return <div className="photo-upload--preview-image image--loading">
        <Spinner />
      </div>;
  }
  return <div className="photo-upload--preview-image photo-upload--image-complete">
      <img src={imageUrl} title={name} onError={() => tagError(genericError)} />
      <ImageRemoveButton removeFile={removeFile} />
    </div>;
};
const PreviewFileList = ({
  files,
  removeFile,
  sizeLimit,
  reportError,
  acceptedTypes
}) => {
  const [taggedFiles, setTaggedFiles] = useState([]);
  const anyFileErrors = Boolean(files && taggedFiles.length);
  useEffect(() => {
    reportError(anyFileErrors);
    return () => {
      reportError(false); //unmount means no files, so no errors
    };
  }, [files, anyFileErrors]);
  const setFileError = errorFile => hasError => {
    if (hasError) {
      setTaggedFiles(taggedFiles => addUnique(taggedFiles, errorFile));
    } else {
      setTaggedFiles(taggedFiles => taggedFiles.filter(file => file !== errorFile));
    }
  };
  return <div className="photo-upload--image--file-list">
      {files.map((file, i) => {
      const {
        size,
        name,
        type
      } = file;
      return <PreviewImage key={`${name}_${i}`} file={file} size={size} name={name} type={type} acceptedTypes={acceptedTypes} delay={10 * (i + 1)} sizeLimit={sizeLimit} reportFileError={setFileError(file)} removeFile={() => removeFile(file)} />;
    })}
    </div>;
};
export const VisualDropArea = ({
  limit = 1,
  inputRef,
  isMultiple,
  errorMessage
}) => {
  const [hasDragOver, setDragOverState] = useState('');

  // handling the detection of events over the input, for visual changes
  useEffect(() => {
    const dragOn = e => {
      if (e.target === inputRef.current) {
        setDragOverState(true);
      } else {
        setDragOverState(false);
      }
    };
    const dragOff = e => {
      if (e.target === inputRef.current) {
        setDragOverState(false);
      }
    };
    const dragOverInput = () => {
      setDragOverState(true);
    };
    const dragOffInput = () => {
      setDragOverState(false);
    };

    // add drop event to cancel file drops outside the input
    inputRef.current.addEventListener('dragenter', dragOverInput, false);
    inputRef.current.addEventListener('dragleave', dragOffInput, false);
    document.addEventListener('drop', dragOff, false);
    document.addEventListener('dragend', dragOff, false);
    document.addEventListener('dragover', dragOn, false);
    return () => {
      // remove event canceling drop events
      document.removeEventListener('drop', dragOff);
      document.removeEventListener('dragend', dragOff);
      document.removeEventListener('dragover', dragOn);
      if (inputRef.current) {
        inputRef.current.removeEventListener('dragenter', dragOverInput, false);
        inputRef.current.removeEventListener('dragleave', dragOffInput, false);
      }
    };
  }, []);
  const className = `photo-upload--drop-container${hasDragOver ? ` has-dragover` : ``}${errorMessage ? ' has-error' : ``}`;
  const uploadRules = ['Supported file types: PNG, JPEG', `Images must be less than ${defaultSizeLimitMB} in size`];
  if (limit > 1) {
    uploadRules.unshift(`${limit} images max`);
  }
  return <div className={className}>
      <div className="photo-upload--wrapper">
        <div className="photo-upload--text-area">
          {`Drop ${isMultiple ? 'your files' : `a single file`} here`} or{' '}
          <span>browse</span>
        </div>
        <ul className="photo-upload--rules">
          {uploadRules.map(rule => <li key={rule}>{rule}</li>)}
        </ul>
      </div>
    </div>;
};
export const ClientPhotoUpload = ({
  value = [],
  accept = defaultTypes,
  limit = 1,
  closeModal,
  update,
  sizeLimit = defaultSizeLimit,
  isMultiple,
  setHasTempFiles
}) => {
  const fileInput = useRef();
  const [currentFiles, setCurrentFiles] = useState(value || []);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasFileError, setHasFileError] = useState(false);
  const acceptedTypes = accept.split(',');
  const isEmpty = !currentFiles || !currentFiles.length;
  const hasMultiple = currentFiles && currentFiles.length > 1;
  const showDropArea = isMultiple && currentFiles && currentFiles.length !== limit || isEmpty;
  const updateFileInput = files => {
    const dt = new DataTransfer();
    files.forEach(file => {
      if (typeof file === 'File') {
        dt.items.add(file);
      }
    });
    fileInput.current.files = dt.files;
  };
  const removeFile = fileToRemove => {
    if (!currentFiles || !currentFiles.length) {
      setCurrentFiles(null);
      return false;
    }
    const cleanedFiles = currentFiles.filter(file => file !== fileToRemove);
    // this isn't supported on pre 14 iOS/Safari but there's no alternate way
    // do it for those browsers except to clear the whole input
    if (!cleanedFiles.length) {
      fileInput.current.value = '';
      setErrorMessage('');
    } else {
      updateFileInput(cleanedFiles);
    }
    setCurrentFiles(cleanedFiles);
  };

  // checking the files added for various errors
  const onFileChange = event => {
    const files = Array.from(event.target.files);
    const numNewFiles = files && files.length || 0;
    const numCurrentFiles = currentFiles && currentFiles.length || 0;
    const overSize = files.filter(({
      size
    }) => size > sizeLimit);
    const lastFile = files[files.length - 1];
    const overFileLimit = isMultiple && numNewFiles + numCurrentFiles > limit;
    if (!isMultiple && overSize.length) {
      event.preventDefault();
      fileInput.current.value = '';
      if (!isMultiple && !isEmpty) {
        setErrorMessage(fileChangeTooLargeError);
      } else {
        setErrorMessage(fileTooLargeError);
      }
      return false;
    } else if (overFileLimit) {
      event.preventDefault();
      setErrorMessage(`You can only upload up to ${limit} files`);
      return false;
    } else if (lastFile && acceptedTypes.indexOf(lastFile.type) === -1) {
      event.preventDefault();
      removeFile(lastFile);
      setErrorMessage(unsupportedFileError);
    } else if (!numNewFiles && numCurrentFiles) {
      updateFileInput(currentFiles);
      setErrorMessage('');
    } else {
      if (isMultiple && numCurrentFiles) {
        //append case, need to override input behavior
        const combinedFiles = currentFiles.concat(files);
        updateFileInput(combinedFiles);
        setCurrentFiles(combinedFiles);
      } else {
        // input already has the correct file, no need to update
        setCurrentFiles(files);
      }
      setErrorMessage('');
    }
  };

  // saving blob urls to the store instead of entire images
  const saveBlobs = () => {
    // TODO: URLrevoke existing values
    if (currentFiles && currentFiles.length) {
      const values = currentFiles.map(file => {
        const {
          url,
          size,
          type,
          name
        } = file;
        return {
          url: url || URL.createObjectURL(file),
          size,
          type,
          name
        };
      });
      update(values);
    } else {
      update(null);
    }
  };
  const capturePhotos = () => {
    saveBlobs();
    closeModal();
  };

  // we want to prevent accidental drops outside the file inputs
  // from replacing the entire page with the dropped image
  useEffect(() => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    const maybeCancelDropEvent = e => {
      const {
        dataTransfer
      } = e;
      if (e.target !== fileInput.current) {
        e.preventDefault();
        dataTransfer.effectAllowed = 'none';
        dataTransfer.dropEffect = 'none';
        return false;
      } else {
        dataTransfer.effectAllowed = 'all';
        dataTransfer.dropEffect = 'move';
      }
    };

    // add drop event to cancel file drops outside the input
    document.addEventListener('drop', maybeCancelDropEvent, false);
    document.addEventListener('dragover', maybeCancelDropEvent, false);
    return () => {
      // remove event canceling drop events
      document.removeEventListener('drop', maybeCancelDropEvent);
      document.removeEventListener('dragover', maybeCancelDropEvent);
    };
  }, []);

  // allows the modal to grow taller when multiple photos are added
  useEffect(() => {
    setHasTempFiles(!isEmpty);
  }, [currentFiles]);
  const s = isMultiple ? `s` : ``;

  // overrides the input field's default title (sometimes seen on hover)
  const inputTitle = !isEmpty ? `${currentFiles.length} file${hasMultiple ? `s` : ``} chosen` : undefined;
  const hasError = hasFileError || String(errorMessage).trim();
  return <div className="portal-modal--ui-container">
      <div className="portal-modal--intro">
        <h2 className="portal-modal--heading">Capture Photo{s}</h2>
        {isMultiple && !isEmpty && <div className="photo-upload--count">
            {currentFiles.length} of {limit}
          </div>}
      </div>
      <div className="photo-upload--area">
        {showDropArea && <VisualDropArea limit={limit} isMultiple={isMultiple} inputRef={fileInput} errorMessage={errorMessage} />}
        <input ref={fileInput} type="file" onChange={onFileChange} onError={e => setErrorMessage(e.message)} accept={accept} className="photo-upload--input" multiple={isMultiple} title={inputTitle} />
      </div>
      {!isMultiple && !isEmpty && <SinglePhotoInfo file={currentFiles[0]} removeFile={() => {
      removeFile(currentFiles[0]);
    }} fileInput={fileInput} reportError={isError => {
      if (isError) {
        removeFile(currentFiles[0]);
        update(null);
        setErrorMessage(genericError);
      }
    }} />}
      {!!String(errorMessage).trim() && <div className="portal-modal--error">
          <i className="foundry-icon icon--exclamation-triangle icon--red"></i>
          {errorMessage}
        </div>}
      {isMultiple && !isEmpty && <PreviewFileList files={currentFiles} acceptedTypes={acceptedTypes} removeFile={removeFile} sizeLimit={sizeLimit} reportError={setHasFileError} />}
      <div className="portal-modal--controls">
        <hr />
        <button className="foundry--button btn--link" onClick={closeModal}>
          Cancel
        </button>
        <button className={`foundry--button button-contained ${hasError ? `button--disabled` : `button--active`}`} disabled={hasError} onClick={capturePhotos}>
          Capture Photo{s}
        </button>
      </div>
    </div>;
};